import { usePage } from '@inertiajs/react';
import classNames from 'classnames';
import { ChangeEvent, ReactNode, SyntheticEvent, useEffect, useState } from 'react';
import { Image, Trash } from 'react-feather';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { setServerErrors, useMutation } from '../../Shared/api';
import { Editor } from '../../Shared/Editor/Editor';
import ImageCropper from '../../Shared/Image/ImageCropper';
import { useNotifier } from '../../Shared/Notifier/NotificationProvider';
import { Locale } from '../../Shared/types';
import { Button } from '../../Shared/UI/Button';
import { Card } from '../../Shared/UI/Card';
import ErrorMessage from '../../Shared/UI/ErrorMessage';
import { FileInput } from '../../Shared/UI/FileInput';
import { Icon } from '../../Shared/UI/Icon';
import { Input } from '../../Shared/UI/Input';
import InputDescription from '../../Shared/UI/InputDescription';
import InputGroup from '../../Shared/UI/InputGroup';
import InputLabel from '../../Shared/UI/InputLabel';
import { InputWrapper } from '../../Shared/UI/InputWrapper';
import PhoneNumberInput from '../../Shared/UI/PhoneNumberInput';
import { Select } from '../../Shared/UI/Select';
import ServerErrors from '../../Shared/UI/ServerErrors';
import { Textarea } from '../../Shared/UI/Textarea';
import { defaultColor } from '../../Shared/useCharityStyle';
import { useRouter } from '../router';
import { PageProps } from '../types';
import Container from '../UI/Container';
import Layout from '../UI/Layout';
import { CharitySettingsLayout } from './CharitySettingsLayout';

export default function SettingsPage() {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const notifier = useNotifier();

  const { charity } = usePage<PageProps>().props;

  const {
    register, setValue, handleSubmit, setError, resetField, watch, control,
    formState: { isSubmitting, errors, defaultValues },
  } = useForm({
    defaultValues: {
      title: charity.title,
      logo: null,
      delete_logo: !charity.logo_url,
      brand_color: charity.brand_color,
      website: charity.website,
      email: charity.email,
      phone: charity.phone,
      description: charity.description,
      image: null,
      delete_image: false,
      privacy_statement: charity.privacy_statement,
      locale: charity.locale,
    },
  });

  const brandColor = watch('brand_color');

  const [saveSettings, { errors: serverErrors }] = useMutation(
    routes.save_settings(),
    {
      preserveState: 'errors',
      onSuccess: () => {
        notifier.notify(t('backend:information_updated'));
      },
      onError: (errors) => {
        setServerErrors(errors, setError, defaultValues);
      },
    }
  );

  const description = watch('description');
  const descriptionLength = description?.length || 0;
  const maxDescriptionLength = 100;

  const [logo, setLogo] = useState<File>();
  const deleteLogo = watch('delete_logo');
  const canDeleteLogo = !deleteLogo && (!!logo || !!charity.logo_url);

  const handleLogoChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLogo(event.target.files?.[0] || undefined);
    resetField('logo'); // Reset errors
    setValue('delete_logo', false);
    setLogoSrc(undefined);
  };

  const resetLogo = () => {
    setLogo(undefined);
    resetField('logo'); // Reset errors
    setValue('delete_logo', true);
    setLogoSrc(undefined);
  };

  const [logoSrc, setLogoSrc] = useState<string>();

  useEffect(() => {
    if (logo) {
      const reader = new FileReader();
      reader.addEventListener('load', () => setLogoSrc(reader.result?.toString() || undefined));
      reader.readAsDataURL(logo);
    }
  }, [logo]);

  const validateLogo = (event: SyntheticEvent<HTMLImageElement>) => {
    const element = event.currentTarget;

    if (element.naturalWidth < 150 || element.naturalHeight < 150) {
      resetLogo();
      setError('logo', { message: t('shared:validation.image_too_small') });
    }
  };

  const [image, setImage] = useState<File>();
  const [croppedImage, setCroppedImage] = useState<File>();
  const deleteImage = watch('delete_image');
  const canDeleteImage = !deleteImage && (!!image || !!charity.image_url);

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    setImage(event.target.files?.[0] || undefined);
    setCroppedImage(undefined);
    resetField('image'); // Reset errors
    setValue('delete_image', false, { shouldDirty: true });
  };

  const handleCropChange = (file: File, element: HTMLImageElement) => {
    if (element.naturalWidth < 800 || element.naturalHeight < 450) {
      resetImage();
      setError('image', { message: t('shared:validation.image_too_small') });
    } else {
      setCroppedImage(file);
    }
  };

  const resetImage = () => {
    setImage(undefined);
    setCroppedImage(undefined);
    resetField('image'); // Reset errors
    setValue('delete_image', true, { shouldDirty: true });
  };

  const submit = async (data: typeof defaultValues) => {
    await saveSettings({
      ...data,
      phone: data?.phone || null, // Cast 'undefined' to 'null' so that it is sent to the server (where it is required)
      logo,
      image: (image && croppedImage) || null,
    });
  };

  return (
    <Container size="md" className="space-y-6">
      <form onSubmit={handleSubmit(submit)}>
        <Card panels header footer>
          <div>
            <h3>
              {t('backend:edit_general_settings')}
            </h3>
          </div>

          <div className="space-y-6">
            {serverErrors && <ServerErrors errors={serverErrors} scrollIntoView />}

            <div>
              <h4 className="font-medium text-slate-500 mb-1">
                {t('backend:general_settings')}
              </h4>
              <div className="text-slate-500">
                {t('backend:charity_settings_description')}
              </div>
            </div>

            <InputGroup>
              <InputLabel required valid={!errors.title} htmlFor="charity_title">
                {t('backend:organisation_name')}
              </InputLabel>
              <Input
                {...register('title')}
                id="charity_title"
                aria-invalid={!!errors.title}
              />
              <ErrorMessage error={errors.title} attribute={t('backend:organisation_name')} />
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="charity_logo">
                {t('backend:logo')}
              </InputLabel>
              <InputDescription>
                {t('backend:charity_logo_description')}
              </InputDescription>
              <div className="max-w-md p-4 border rounded-md border-slate-250">
                <div
                  className="flex items-center justify-center p-12 mb-4 transition-all rounded-sm bg-slate-50"
                >
                  {!logo && !deleteLogo && charity.logo_url && (
                    <img
                      src={charity.logo_url}
                      alt={charity.title}
                      className="w-full rounded-sm"
                    />
                  )}
                  {logo && logoSrc && (
                    <img
                      src={logoSrc}
                      alt={charity.title}
                      className="w-full rounded-sm"
                      onLoad={validateLogo}
                    />
                  )}
                  {((!logo && !charity.logo_url) || deleteLogo) && (
                    <Icon className="!w-12 !h-12 text-slate-250">
                      <Image strokeWidth={1} />
                    </Icon>
                  )}
                </div>
                <div className="flex space-x-2">
                  <FileInput
                    {...register('logo')}
                    accept=".jpg,.jpeg,.png"
                    id="edit_charity_logo"
                    aria-invalid={!!errors.logo}
                    onChange={handleLogoChange}
                  >
                    <Icon className="mr-2">
                      <Image />
                    </Icon>
                    {t('shared:form.select')}
                  </FileInput>
                  {canDeleteLogo && (
                    <Button onClick={() => resetLogo()} variant="tertiary">
                      <Icon className="mr-2">
                        <Trash />
                      </Icon>
                      {t('shared:form.delete')}
                    </Button>
                  )}
                </div>
              </div>
              <ErrorMessage error={errors.logo} attribute={t('backend:logo')} />
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="charity_description">
                {t('backend:charity_description')}
              </InputLabel>
              <InputDescription>
                {t('backend:charity_description_description')}
                {' '}
                <span className={classNames('bg-blue-200 text-blue-700 px-1 inline-block text-sm rounded-sm', descriptionLength === 0 && 'opacity-50')}>
                  <strong >{descriptionLength}</strong> / {maxDescriptionLength}
                </span>
              </InputDescription>
              <Textarea
                {...register('description')}
                id="charity_description"
                aria-invalid={!!errors.description}
                rows={2}
                maxLength={maxDescriptionLength}
              />
              <ErrorMessage error={errors.description} attribute={t('backend:description')} />
            </InputGroup>

            <InputGroup>
              <InputLabel valid={!errors.website} htmlFor="charity_website">
                {t('backend:website')}
              </InputLabel>
              <Input
                {...register('website')}
                id="charity_website"
                aria-invalid={!!errors.website}
              />
              <ErrorMessage error={errors.website} attribute={t('backend:website')} />
            </InputGroup>

            <InputGroup>
              <InputLabel valid={!errors.email} htmlFor="charity_email">
                {t('backend:email_address')}
              </InputLabel>
              <Input
                {...register('email')}
                id="charity_email"
                aria-invalid={!!errors.email}
              />
              <ErrorMessage error={errors.email} attribute={t('backend:email_address')} />
            </InputGroup>

            <InputGroup>
              <InputLabel valid={!errors.phone} htmlFor="charity_phone">
                {t('backend:phone_number')}
              </InputLabel>
              <Controller
                control={control}
                name="phone"
                render={({ field: { onChange, onBlur, value } }) => (
                  <PhoneNumberInput
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    id="charity_phone"
                    ariaInvalid={!!errors.phone}
                  />
                )}
              />
              <ErrorMessage error={errors.phone} attribute={t('backend:phone_number')} />
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="charity_brand_color">
                {t('backend:brand_color')}
              </InputLabel>
              <InputWrapper>
                <span>
                  <div className="w-4 h-4 rounded-sm" style={{ background: brandColor || defaultColor }} />
                </span>
                <Input
                  {...register('brand_color')}
                  id="charity_brand_color"
                  className="pl-9"
                  aria-invalid={!!errors.brand_color}
                  placeholder={defaultColor}
                />
              </InputWrapper>
              <ErrorMessage error={errors.brand_color} attribute={t('backend:brand_color')} />
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="edit_image">
                {t('backend:default_image')}
              </InputLabel>
              <InputDescription>
                {t('backend:project_image_description')}
              </InputDescription>
              <div className="max-w-[610px] p-4 border rounded-md border-slate-250">
                <div
                  className="flex items-center justify-center mb-4 transition-all rounded-sm bg-slate-50 aspect-video"
                >
                  {!image && !deleteImage && charity.image_url && (
                    <img
                      src={charity.image_url}
                      alt={charity.title}
                      className="object-cover w-full h-full rounded-sm"
                    />
                  )}
                  {image && (
                    <ImageCropper
                      image={image}
                      onChange={handleCropChange}
                      aspect={16 / 9}
                    />
                  )}
                  {((!image && !charity.image_url) || deleteImage) && (
                    <Icon className="!w-12 !h-12 text-slate-250">
                      <Image strokeWidth={1} />
                    </Icon>
                  )}
                </div>
                <div className="flex space-x-2">
                  <FileInput
                    {...register('image')}
                    accept=".jpg,.jpeg,.png"
                    id="edit_image"
                    aria-invalid={!!errors.image}
                    onChange={handleImageChange}
                  >
                    <Icon className="mr-2">
                      <Image />
                    </Icon>
                    {t('shared:form.select')}
                  </FileInput>
                  {canDeleteImage && (
                    <Button onClick={() => resetImage()} variant="tertiary">
                      <Icon className="mr-2">
                        <Trash />
                      </Icon>
                      {t('shared:form.delete')}
                    </Button>
                  )}
                </div>
              </div>
              <ErrorMessage error={errors.image} attribute={t('backend:image')} />
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="charity_privacy_statement">
                {t('backend:privacy_statement')}
              </InputLabel>
              <InputDescription>
                {t('backend:privacy_statement_privacy_statement')}
              </InputDescription>
              <Controller
                control={control}
                name="privacy_statement"
                render={({ field: { onChange, onBlur, value } }) => (
                  <Editor
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                )}
              />
              <ErrorMessage error={errors.privacy_statement} attribute={t('backend:privacy_statement')} />
            </InputGroup>

            <InputGroup>
              <InputLabel required valid={!errors.locale} htmlFor="edit_campaign_locale">
                {t('backend:language')}
              </InputLabel>
              <Select
                {...register('locale', { required: true })}
                id="edit_campaign_locale"
                aria-invalid={!!errors.locale}
              >
                <option value={Locale.EN}>
                  {t(`shared:locales.${Locale.EN}`)}
                </option>
                <option value={Locale.NL}>
                  {t(`shared:locales.${Locale.NL}`)}
                </option>
              </Select>
              <ErrorMessage error={errors.locale} attribute={t('backend:language')} />
            </InputGroup>
          </div>
          <div className="text-right">
            <Button
              type="submit"
              loading={isSubmitting}
              className="ml-auto"
            >
              {t('backend:save')}
            </Button>
          </div>
        </Card>
      </form>
    </Container>
  );
}

SettingsPage.layout = (page: ReactNode) => <Layout><CharitySettingsLayout>{page}</CharitySettingsLayout></Layout>;
